import clsx from "clsx";
import Select, {
  type CSSObjectWithLabel,
  type SingleValue,
} from "react-select";
import type { CurrencyPrimitive } from "@/api/cashier";
import { Icon } from "@/components/general/icon/Icon";
import type { EIcon } from "@/utils/enums";
import style from "./CurrencySelect.module.css";
import globe from "@/assets/icons/globe-icon.svg";

type CurrencySelectProps = {
  testId?: string;
  currencies: CurrencyPrimitive[];
  selectedCurrency: CurrencyPrimitive;
  setSelectedCurrency: (currency: CurrencyPrimitive) => void;
  disabled?: boolean;
};

export const CurrencySelect = ({
  testId,
  currencies,
  selectedCurrency,
  setSelectedCurrency,
  disabled,
}: CurrencySelectProps) => {
  const currencyOnChange = (
    newValue: SingleValue<{ value: CurrencyPrimitive; label: string }>
  ) => {
    if (!newValue || newValue.value.code === selectedCurrency.code) return;
    setSelectedCurrency(newValue?.value);
  };

  const amountFieldStyles = () => {
    return {
      control: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        background: "#FFF",
        borderRadius: "50px",
        border: "1px solid #03142E1A",
        minHeight: "unset",
        padding: "4px",
        cursor: "pointer",
        boxSizing: "content-box",
        alignItems: "center",
        lineHeight: "1",
      }),
      container: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        cursor: "pointer",
      }),
      valueContainer: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        padding: 0,
        cursor: "pointer",
      }),
      indicatorsContainer: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        display: "flex",
        alignItems: "center",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: "8px",
        height: "100%",
      }),
      dropdownIndicator: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
        ...base,
        padding: 0,
        width: "16px",
        color: "black",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }),
    };
  };

  return (
    <Select
      unstyled
      styles={amountFieldStyles()}
      classNames={{
        menu: () => style.menu,
        option: () => style.menuOption,
      }}
      data-testId={testId}
      value={{
        value: selectedCurrency,
        label: selectedCurrency?.code,
      }}
      onChange={currencyOnChange}
      options={currencies.map((currency) => {
        return {
          value: currency,
          label: currency?.code,
        };
      })}
      isClearable={false}
      isSearchable={false}
      aria-live="off"
      components={{
        IndicatorSeparator: () => null,
      }}
      isDisabled={disabled}
      formatOptionLabel={(currency, { context }) => {
        return (
          <div
            className={clsx(
              "cursor-pointer",
              style["country-option"],
              context === "menu" &&
                currency.value.code === selectedCurrency.code &&
                style.selected
            )}
          >
            <Icon
              name={
                `flags/${currency.value.code?.toLocaleLowerCase()}-flag` as EIcon
              }
              fallback={globe}
            />

            <span className="cursor-pointer">{currency.label}</span>
          </div>
        );
      }}
    />
  );
};
